import type { BoardItem, FeedbackItem, MergeResult } from '@/lib/apiClient';
import { firstLine } from '@/lib/feedbackMine';
import { plural } from '@/lib/plural';
import { buildIndex, suggestFor, type SimilarIndex, type SuggestOptions, type Suggestion } from '@/lib/similarFeedback';

/**
 * The rules behind the Feedback tab's Merge action (docs/feedback-loop-plan.md §6.5, §6.12, §6.14), kept out of the components so each is testable on its own.
 *
 * The API decides what is allowed (`POST /feedback/:id/merge` refuses with a reason, before it writes anything). These mirror it only so the screen offers what
 * will work, and so the preview says, before anything happens, what a merge does to the people it touches.
 */

type Mergeable = Pick<FeedbackItem, 'duplicateOf' | 'boardVisibility' | 'status' | 'closedReason'>;

/** An item has been merged into another one: it is a closed duplicate, hidden on purpose, and none of the actions below apply to it any more. */
export const isMerged = (i: Pick<FeedbackItem, 'duplicateOf'>): boolean => i.duplicateOf != null;

/**
 * Whether Merge is offered. It mirrors what the API will accept of the ITEM:
 *  - not merged already (`already_merged`);
 *  - not private (`private`): its sender asked to keep it to themselves and the admins, and no admin action changes that;
 *  - not closed for another reason (`closed_other`): closed as done, as not planned, or with no reason says nothing about it being a duplicate. One closed AS
 *    a duplicate (a developer closed its issue as one on GitHub) can be merged, which is the common case.
 * A board state the API could not read (`null`) is not "fine": nothing is offered on what is not known.
 */
export const canMerge = (i: Mergeable): boolean =>
  !isMerged(i)
  && i.boardVisibility != null && i.boardVisibility !== 'private'
  && !(i.status === 'closed' && i.closedReason !== 'duplicate');

/**
 * What the Status column says of an item that was merged: where it went, in words an admin can find in the list (the start of that request), or just that it
 * was combined when that request is not in the list the screen holds (the list is the newest items, not all of them).
 *
 * ⚠️ "COMBINED INTO …" IS ONLY TRUE OF AN ITEM THAT IS STILL CLOSED. One reopened since (a developer reopened its issue on GitHub; the merge keeps
 * `duplicateOf`, §6.12) is OPEN, and the row offers Done and Won't do beside it, so it says that it was reopened, and what it was combined into, and not
 * that it is combined.
 */
export function combinedLabel(
  i: Pick<FeedbackItem, 'duplicateOf'> & Partial<Pick<FeedbackItem, 'status'>>,
  items: readonly Pick<FeedbackItem, 'id' | 'message'>[],
): string | null {
  if (i.duplicateOf == null) return null;
  const to = items.find((o) => o.id === i.duplicateOf);
  const where = to ? `“${firstLine(to.message, 50)}”` : 'another request';
  return i.status === 'open' ? `Reopened after it was combined into ${where}` : `Combined into ${where}`;
}

// ─── Choosing the other request ──────────────────────────────────────────────────────

/**
 * What an admin can merge INTO: a request on the board that is still open. Nothing else will do (`target_not_public`, `target_not_open`: everyone merged into
 * an item reads its text, and a closed one will never tell them anything), and not the item itself (`same`). The board lists exactly the public items, so
 * this is the board's open ones.
 */
export const mergeCandidates = (board: readonly BoardItem[], itemId: number): BoardItem[] =>
  board.filter((b) => b.status === 'open' && b.id !== itemId);

/**
 * How the picker suggests the other request: the same matcher the members' "Similar requests" use, with LOOSER limits, because an admin reads each one
 * before confirming and a miss costs a merge that is never made. `threshold` sits between what the keyword matcher scored on an item only loosely about
 * the same thing and on one about the same thing in other words (docs/feedback-loop-plan.md §6.3 measured about 0.13 and about 0.25 to 0.37), so a
 * request on the same topic is offered and one only sharing a word is not. It is a judgment from those scores, not a measurement of its own: tune it
 * after it has been used. `minWords` is lower than the members' because the text is a request somebody already sent, however short.
 */
export const MERGE_SUGGEST: SuggestOptions = { threshold: 0.2, max: 5, minWords: 3 };

/** The index the suggestions and nothing else are matched against: built once per list of candidates. */
export const indexCandidates = (candidates: readonly BoardItem[]): SimilarIndex => buildIndex(candidates);

/** The requests most like `text`, best first. Empty until the text has enough to go on. */
export const suggestTargets = (index: SimilarIndex, text: string): Suggestion[] => suggestFor(index, text, MERGE_SUGGEST);

/** How many results a search shows at once: more is a list nobody reads, and "narrow it" is the useful answer. */
export const SEARCH_SHOWN = 20;

/**
 * The candidates whose message contains EVERY word typed, in the order given (open requests, newest first). Case does not matter and a word may be part
 * of a longer one. Nothing typed is nothing found, not everything: the suggestions are the starting point, and a search is for what they missed.
 */
export function searchTargets(candidates: readonly BoardItem[], query: string, limit = SEARCH_SHOWN): { shown: BoardItem[]; total: number } {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return { shown: [], total: 0 };
  const found = candidates.filter((c) => {
    const text = c.message.toLowerCase();
    return terms.every((t) => text.includes(t));
  });
  return { shown: found.slice(0, limit), total: found.length };
}

// ─── What a merge does, said before it happens ───────────────────────────────────────

export type PreviewLine = { tone: 'info' | 'warn'; text: string };

/**
 * What merging this item does, one line each, for the preview. Every line is true of what the API will do (docs/feedback-loop-plan.md §6.12):
 * the item closes as a duplicate and leaves the board; everyone who follows it, and the person who sent it, is added to the other request (copied, not
 * moved: nobody is deleted from this one); the sender is emailed once and NOBODY ELSE is; its GitHub issue gets a comment and is closed as not planned;
 * replies stay where they are; and nothing here can be undone.
 *
 * `targetIssue` is the other request's issue number when the admin's list has it (the board does not carry it). Without it the line says "the other request".
 */
export function previewLines(
  item: Pick<FeedbackItem, 'status' | 'boardVisibility' | 'followerCount' | 'githubIssueNumber' | 'replies'>,
  targetIssue?: number | null,
): PreviewLine[] {
  const lines: PreviewLine[] = [];
  const closed = item.status === 'closed';

  const where = item.boardVisibility === 'public' ? ' It comes off the board.' : item.boardVisibility === 'unreviewed' ? ' It leaves To review.' : '';
  lines.push({ tone: 'info', text: `${closed ? 'It is already closed as a duplicate, so it is not closed again.' : 'It is closed as a duplicate.'}${where}` });

  const n = item.followerCount;
  if (n == null) {
    lines.push({ tone: 'info', text: 'Who follows it could not be loaded. Everyone who does, and the person who sent it, is added to the other request.' });
  } else if (n === 0) {
    lines.push({ tone: 'info', text: 'Nobody follows it. The person who sent it is still added to the other request.' });
  } else {
    lines.push({
      tone: 'info',
      text: `${plural(n, 'person follows', 'people follow')} it. Everyone who follows it, and the person who sent it, is added to the other request, except anyone who already follows it. They keep this one under My feedback.`,
    });
  }

  lines.push({ tone: 'info', text: 'The person who sent it gets one email saying it was combined with an existing request, unless that is you: nobody is emailed about what they did themselves. Nobody else is emailed, including anyone who said “me too”.' });

  if (item.githubIssueNumber != null) {
    const theOther = targetIssue != null ? `#${targetIssue}` : 'the other request';
    lines.push({
      tone: 'info',
      text: `On GitHub, issue #${item.githubIssueNumber} gets a comment saying it duplicates ${theOther}${closed ? '. It is not closed again.' : ', and is closed as not planned.'}`,
    });
  } else {
    lines.push({ tone: 'info', text: 'It has no GitHub issue, so nothing is sent to GitHub.' });
  }

  const replies = item.replies?.length ?? 0;
  if (replies > 0) lines.push({ tone: 'info', text: `The ${plural(replies, 'reply', 'replies')} already on it stay with it and are not copied to the other request.` });

  lines.push({ tone: 'warn', text: 'This cannot be undone from here.' });
  return lines;
}

// ─── What it did ─────────────────────────────────────────────────────────────────────

export type MergeNote = { tone: 'ok' | 'warn'; text: string };

/** Whether anything on GitHub needs an admin's look: the comment did not land, or the issue is not closed (yet). */
const githubNeedsLook = (r: MergeResult, hadIssue: boolean): boolean =>
  r.github.comment === 'failed' || (hadIssue && r.github.comment === 'skipped') || !['pushed', 'not_needed'].includes(r.github.close);

/**
 * The heading over what a merge did: DERIVED FROM THE ANSWER, never fixed. A merge that went into a different request than was chosen, or that left something on
 * GitHub undone, is not simply "Merged", and a heading that says so over a body that says otherwise is the one a skimming admin believes.
 */
export function mergeHeading(r: MergeResult, hadIssue: boolean): string {
  if (r.mergedInto !== r.requestedInto) return 'Merged, into a different request than you chose';
  if (githubNeedsLook(r, hadIssue)) return 'Merged, but GitHub needs a look';
  return 'Merged';
}

/**
 * What a merge did, in plain sentences. ⚠️ SAYS WHAT HAPPENED, not what was asked: the sender's email is sent in the background and the answer does not say
 * whether it went, so this says that and does not claim it; a comment that was not posted, or an issue not closed yet, is said as such, because silence after an
 * action that cannot be undone reads as success.
 */
export function describeMerge(r: MergeResult, hadIssue: boolean): MergeNote[] {
  const notes: MergeNote[] = [];
  if (r.mergedInto !== r.requestedInto) {
    notes.push({ tone: 'warn', text: 'The request you chose had itself been merged into another one, so this went into that one instead.' });
  }

  if (r.followersMoved > 0) {
    notes.push({ tone: 'ok', text: `${plural(r.followersMoved, 'person was', 'people were')} added to the other request.` });
    if (r.notMoved > 0) {
      notes.push({ tone: 'ok', text: `${plural(r.notMoved, 'other person', 'other people')} already followed it or no longer ${r.notMoved === 1 ? 'has an account' : 'have accounts'}, and ${r.notMoved === 1 ? 'was' : 'were'} left as they were.` });
    }
  } else if (r.notMoved > 0) {
    notes.push({ tone: 'ok', text: `Nobody was added: ${r.notMoved === 1 ? 'the one person' : 'everyone'} already followed the other request or no longer has an account.` });
  } else {
    notes.push({ tone: 'ok', text: 'Nobody was added: nobody followed it.' });
  }

  notes.push({ tone: 'ok', text: r.closed ? 'It was closed as a duplicate.' : 'It was already closed as a duplicate, so it was not closed again.' });

  if (r.github.comment === 'posted') notes.push({ tone: 'ok', text: 'The comment was posted on its GitHub issue.' });
  else if (r.github.comment === 'failed') notes.push({ tone: 'warn', text: 'The comment could not be posted on its GitHub issue. Nothing tries again: add it yourself if you want it there.' });
  else if (hadIssue) notes.push({ tone: 'warn', text: 'The comment was not posted on its GitHub issue: GitHub is not connected, or the issue is not in this repository.' });

  switch (r.github.close) {
    case 'pushed': notes.push({ tone: 'ok', text: 'Its GitHub issue was closed as not planned.' }); break;
    case 'pending': notes.push({ tone: 'warn', text: 'Its GitHub issue is not closed yet. The change is saved here and retried every night, or press Sync with GitHub.' }); break;
    case 'skipped': notes.push({ tone: 'warn', text: 'Its GitHub issue was not closed: GitHub is not connected. The change is saved here and sent once it is.' }); break;
    case 'superseded': notes.push({ tone: 'warn', text: 'Its GitHub issue changed again while it was being closed. Look at the Issue column.' }); break;
    case 'not_needed': break;
  }

  notes.push({ tone: 'ok', text: 'The email to the person who sent it, unless that is you, is sent in the background, and this screen is not told whether it arrived.' });
  return notes;
}
